import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import type { OrderSummary } from '@/infrastructure/OMSClient';
import type { DownloadShippingLabelRequest, DownloadShippingLabelResponse } from '@/infrastructure/ShippingClient';
import { fetchFedExShippingLabel } from '@/pages/oms/orders/utils/shippingLabelDownload';
import { FEDEX_LABEL_HEIGHT_MM, FEDEX_LABEL_WIDTH_MM } from '@/pages/oms/orders/utils/fedExZpl';
import { DeleoPackingVerificationPage } from '@/pages/oms/orders/[id]/DeleoPackingVerificationPage';

interface FedExLabelPrintModalProps {
  order: OrderSummary;
  downloadLabel: (request: DownloadShippingLabelRequest) => Promise<DownloadShippingLabelResponse>;
  onClose: () => void;
}

const PRINT_STYLE = `
  @page fedex-sheet { size: 101.6mm 152.4mm !important; margin: 0 !important; }
  @media print {
    html, body, .fedex-print-set { width: 101.6mm !important; margin: 0 !important; padding: 0 !important; }
    .fedex-carrier-page, .fedex-packing-sheet {
      page: fedex-sheet; width: 101.6mm !important; height: 152.4mm !important;
      break-after: page; break-inside: avoid; margin: 0 !important; padding: 0 !important;
      position: relative; overflow: hidden; box-sizing: border-box;
    }
    .fedex-print-set > :last-child { break-after: auto; }
    /* A 150mm-wide flow box makes thermal-printer output shrink all carrier pages.
       Rotate the unchanged packing sheet INSIDE a stock-sized, clipped flow box. */
    .fedex-packing-sheet .deleo-verification-page {
      position: absolute !important; left: 0.8mm; top: 1.2mm; margin: 0 !important;
      transform: translateX(100mm) rotate(90deg); transform-origin: top left;
      break-before: auto !important; break-after: auto !important;
    }
  }
`;

/** Mounted with the current order/shipment key so pending work never crosses shipment identities. */
export function FedExLabelPrintModal({ order, downloadLabel, onClose }: FedExLabelPrintModalProps) {
  const [snapshot] = useState(() => ({
    ...order,
    ...(order.shippingData ? { shippingData: { ...order.shippingData } } : {}),
    lineItems: order.lineItems.map((item) => ({ ...item })),
  }));
  const downloadRef = useRef(downloadLabel);
  const contentRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(false);
  const [pages, setPages] = useState<string[]>([]);
  const [loaded, setLoaded] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    activeRef.current = true;
    setPages([]);
    setLoaded(new Set());
    setError('');
    const prepare = async () => {
      try {
        const providerOrderId = snapshot.shippingData?.providerOrderId;
        if (!providerOrderId || !/^\d+$/.test(snapshot.orderId)) {
          throw new Error('Missing shipment identity');
        }
        const label = await fetchFedExShippingLabel(downloadRef.current, snapshot.orderId, providerOrderId);
        if (controller.signal.aborted) {
          return;
        }
        if (
          label.providerOrderId !== providerOrderId ||
          label.format !== 'ZPLII' ||
          !label.labelData ||
          label.labelData.length > 180_000
        ) {
          throw new Error('Invalid shipment label');
        }
        // Keep Worker URL sourcemap processing out of the large main bundle.
        const { renderFedExZpl } = await import('@/pages/oms/orders/utils/renderFedExZpl');
        if (controller.signal.aborted) {
          return;
        }
        const images = await renderFedExZpl(atob(label.labelData), controller.signal);
        if (!controller.signal.aborted) {
          setPages(images);
        }
      } catch {
        if (!controller.signal.aborted) {
          setError(
            'Unable to prepare the complete FedEx label set. No labels were printed. Retry or contact support; the original shipment has not been changed.',
          );
        }
      }
    };
    void prepare();
    return () => {
      activeRef.current = false;
      controller.abort();
    };
  }, [snapshot, attempt]);

  const canPrint = !error && pages.length > 0 && loaded.size === pages.length;
  const print = useReactToPrint({
    contentRef,
    pageStyle: PRINT_STYLE,
    documentTitle: `FedEx-${snapshot.orderId}`,
    onBeforePrint: async () => {
      if (!canPrint || !activeRef.current) {
        throw new Error('The complete label set is not ready');
      }
      await document.fonts?.ready;
      if (!activeRef.current) {
        throw new Error('Label printing was cancelled');
      }
    },
    onPrintError: () =>
      setError('The print dialog could not be prepared. No shipment changes were made. Please retry.'),
  });

  return (
    <Modal show onHide={onClose} size="lg" aria-labelledby="fedex-label-title">
      <Modal.Header closeButton>
        <Modal.Title id="fedex-label-title">FedEx labels — OMS #{snapshot.orderId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Use 4 × 6 inch (101.6 × 152.4 mm) paper for every page, at actual size (100%), with no margins, headers, or
          footers. Packing pages are rotated, not resized. Confirm the physical output and scan the barcodes before use.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}
        {!error && pages.length === 0 && (
          <div role="status">
            <Spinner size="sm" /> Preparing labels…
          </div>
        )}
        {!error && pages.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <div ref={contentRef} className="fedex-print-set">
              <style>{PRINT_STYLE}</style>
              {pages.map((source, index) => (
                <div
                  key={`${attempt}-${index}`}
                  className="fedex-carrier-page"
                  style={{ width: `${FEDEX_LABEL_WIDTH_MM}mm`, height: `${FEDEX_LABEL_HEIGHT_MM}mm` }}
                >
                  <img
                    src={source}
                    alt={`FedEx carrier label ${index + 1}`}
                    style={{ width: '100%', height: '100%', display: 'block', imageRendering: 'pixelated' }}
                    onLoad={() => setLoaded((previous) => new Set(previous).add(index))}
                    onError={() =>
                      setError(
                        'A carrier label image failed to load. Printing the incomplete set is disabled. Please retry.',
                      )
                    }
                  />
                </div>
              ))}
              <DeleoPackingVerificationPage
                order={snapshot}
                wrapPage={(page) => (
                  <div key={page.key} className="fedex-packing-sheet">
                    {page}
                  </div>
                )}
              />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {error && (
          <Button variant="outline-primary" onClick={() => setAttempt((value) => value + 1)}>
            Retry
          </Button>
        )}
        <Button
          variant="primary"
          disabled={!canPrint}
          onClick={() => {
            if (canPrint) {
              print();
            }
          }}
        >
          Print / Reprint labels
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
